import { forwardRef } from 'react';
import type { ComponentType, ElementType, ReactElement, Ref } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import { STYLE_PROP_NAMES, toVendor } from './styles';
import type { Styleable, Styles } from './styles';

/**
 * Turn a vendor component into one of ours.
 *
 * Four things are normalised here, and they are the four whose vendor spelling
 * would mislead after a swap:
 *
 *   sx        -> styles / vendorStyles   this application's style vocabulary
 *   size      -> sm | md | lg            't-shirt' sizes, not the vendor's words
 *   color     -> tone                    it takes palette roles, not colours
 *   variant   -> emphasis                how loud the control is
 *
 * Everything else passes through, which is right for props that are ordinary
 * words — `open`, `disabled`, `label`, `value`, `onChange`, `severity`. Renaming
 * those would be churn that buys nothing: a replacement library calls them the
 * same thing, because they are what the concepts are called.
 *
 * The pass-through props keep their types from the vendor. That is the honest
 * limit of this seam: those type references are real coupling, and what the seam
 * guarantees is that all of it lives in this directory instead of at 1,034 call
 * sites.
 */

type Vendor = {
  sx?: SxProps<Theme>;
  size?: unknown;
  color?: unknown;
  variant?: unknown;
};

export type ControlSize = 'sm' | 'md' | 'lg';

export type Tone =
  | 'primary' | 'secondary' | 'neutral'
  | 'success' | 'error' | 'warning' | 'info'
  | 'inherit' | 'default';

/** How loud a control is. Not every component supports all three. */
export type Emphasis = 'solid' | 'outline' | 'plain';

export const SIZE = { sm: 'small', md: 'medium', lg: 'large' } as const;

/**
 * `emphasis` spells out to a different vendor word per component family — a
 * filled Chip, an elevated Card and a contained Button are the same intent said
 * three ways. The map is the translation, so call sites say the intent.
 */
export const EMPHASIS = {
  button:  { solid: 'contained', outline: 'outlined', plain: 'text' },
  chip:    { solid: 'filled',    outline: 'outlined', plain: 'filled' },
  surface: { solid: 'elevation', outline: 'outlined', plain: 'elevation' },
  alert:   { solid: 'filled',    outline: 'outlined', plain: 'standard' },
} as const;

export type EmphasisFamily = keyof typeof EMPHASIS;

interface Options {
  /** Accept `size="sm"` and translate. Omit for components whose size is a number of pixels. */
  size?: boolean;
  /** Accept `tone=` in place of the vendor's `color=`. */
  tone?: boolean;
  /** Accept `emphasis=` in place of the vendor's `variant=`, using this family's spelling. */
  emphasis?: EmphasisFamily;
  /**
   * Props to put back after the style vocabulary is stripped, because for this
   * component the name is its own prop rather than a style: a Dialog's
   * `maxWidth` is a width preset, an InputAdornment's `position` is which end of
   * the field it sits at, an icon's `fontSize` is a t-shirt size.
   */
  keep?: readonly string[];
  /**
   * Props the application passes that the vendor has since moved into its
   * `slotProps` bag: `{ inputProps: 'htmlInput' }` sends `inputProps={…}` on to
   * `slotProps={{ htmlInput: … }}`.
   *
   * These are compatibility aliases, and they are the clearest illustration of
   * what this seam is for: the vendor removed five prop names at once, they were
   * in use at 47 call sites, and the translation is this map.
   */
  slots?: Readonly<Record<string, string>>;
}

type Translated<O extends Options> =
  & { as?: ElementType }
  & (O['size'] extends true ? { size?: ControlSize } : Record<never, never>)
  & (O['tone'] extends true ? { tone?: Tone } : Record<never, never>)
  & (O['emphasis'] extends EmphasisFamily ? { emphasis?: Emphasis } : Record<never, never>);

/**
 * The vendor also accepts its whole style vocabulary as top-level props on some
 * components — `<Box display="flex" mb={2}>`. Leaving those in place would keep
 * a second, vendor-shaped way to style things that a replacement would not
 * understand, so they come off the public surface here and the call sites say
 * `styles={{ display: 'flex', mb: 2 }}` instead.
 */
type Stripped<P, O extends Options> = Omit<
  P,
  | 'sx'
  | 'component'
  | keyof Styles
  | (O['size'] extends true ? 'size' : never)
  | (O['tone'] extends true ? 'color' : never)
  | (O['emphasis'] extends EmphasisFamily ? 'variant' : never)
>;

type Kept<P, O extends Options> =
  O['keep'] extends readonly string[]
    ? Pick<P, Extract<O['keep'][number], keyof P>>
    : Record<never, never>;

type Aliased<O extends Options> =
  O['slots'] extends Readonly<Record<string, string>>
    ? { [K in keyof O['slots']]?: Record<string, unknown> }
    : Record<never, never>;

export type Wrapped<P, O extends Options = Record<never, never>> =
  Stripped<P, O> & Kept<P, O> & Aliased<O> & Translated<O> & Styleable;

/**
 * Prepare a legacy prop bag for the vendor's `slotProps`.
 *
 * Some of these bags used to be handed to a component that took style
 * properties as props — `primaryTypographyProps={{ whiteSpace: 'normal' }}` —
 * and the vendor has since stopped accepting those. Forwarded verbatim they end
 * up as attributes on a DOM node: React warns, and the style silently does
 * nothing. Style keys therefore move into the slot's own style prop.
 */
function asSlotProps(value: unknown): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const entries = Object.entries(value as Record<string, unknown>);
  const style: Record<string, unknown> = {};
  const rest: Record<string, unknown> = {};
  for (const [k, v] of entries) {
    (STYLE_PROP_NAMES.has(k) ? style : rest)[k] = v;
  }
  if (!Object.keys(style).length) return value;
  const existing = rest.sx;
  return {
    ...rest,
    sx: existing ? [style, existing] : style,
  };
}

export function wrap<P extends Vendor, const O extends Options = Record<never, never>>(
  vendor: ComponentType<P>,
  displayName: string,
  options?: O,
) {
  const W = forwardRef<unknown, Record<string, unknown>>(function W(props, ref) {
    const { styles, vendorStyles, size, tone, emphasis, as, ...rest } =
      props as Styleable & Record<string, unknown>;

    const translated: Record<string, unknown> = { ...rest };
    // `as` everywhere rather than the vendor's `component`: it is what the rest
    // of the ecosystem calls this, and the vendor's polymorphic types do not
    // even expose `component` through the props type we wrap.
    if (as !== undefined) translated.component = as;
    if (options?.size) {
      if (size !== undefined) translated.size = SIZE[size as ControlSize];
    } else if (size !== undefined) {
      translated.size = size;
    }
    if (options?.tone && tone !== undefined) translated.color = tone;
    if (options?.emphasis && emphasis !== undefined) {
      translated.variant = EMPHASIS[options.emphasis][emphasis as Emphasis];
    }
    if (options?.slots) {
      const bag: Record<string, unknown> = { ...(translated.slotProps as object) };
      let moved = false;
      for (const [alias, slot] of Object.entries(options.slots)) {
        const value = translated[alias];
        if (value !== undefined) {
          bag[slot] = asSlotProps(value);
          delete translated[alias];
          moved = true;
        }
      }
      if (moved) translated.slotProps = bag;
    }

    const C = vendor as unknown as ElementType;
    return <C {...translated} ref={ref} sx={toVendor(styles, vendorStyles)} />;
  });
  W.displayName = displayName;
  return W as unknown as (props: Wrapped<P, O> & { ref?: Ref<unknown> }) => ReactElement | null;
}
